import React, { useEffect } from 'react';
import { ChevronDown, ChevronRight, Eye, EyeOff, Unlock, Lock } from 'lucide-react';
import CampaignRow from './pacing-table-campaign-row';
import { formatDateForDisplay } from '../utils/date-utils';
import { AdvertiserNode } from '../types/pacing.types';
import { PacingViewApiResponse } from '../endpoints/pacing.types';
import { computeStatus } from '../utils/pacing-utils';
import { useUpdateLineItemMutation } from '../endpoints/pacing.endpoints';

interface AdvertiserRowProps {
  view?: PacingViewApiResponse;
  advertiser: AdvertiserNode;
  showAll: boolean;
}

const AdvertiserRow: React.FC<AdvertiserRowProps> = ({ advertiser, view, showAll }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [advertiserStatus, setAdvertiserStatus] = React.useState(advertiser.agg.status);
  const [isVisible, setIsVisible] = React.useState(advertiser.agg.is_visible == undefined ? true : advertiser.agg.is_visible);
  const [isLocked, setIsLocked] = React.useState(advertiser.agg.is_locked == undefined ? false : advertiser.agg.is_locked);

  const [updateLineItem, { isLoading: isUpdating }] = useUpdateLineItemMutation();

  useEffect(() => {
    const daysRemaining = Math.ceil((new Date(advertiser.agg.end_date ?? '').getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    setAdvertiserStatus(computeStatus(daysRemaining, advertiser.agg.daily_budget, advertiser.agg.spend, advertiser.agg.budget));
    setIsVisible(advertiser.agg.is_visible == undefined ? true : advertiser.agg.is_visible);
    setIsLocked(advertiser.agg.is_locked == undefined ? false : advertiser.agg.is_locked);
  }, [advertiser.agg.status, advertiser.agg.daily_budget, advertiser.agg.spend, advertiser.agg.budget, advertiser.agg.end_date, advertiser.agg.start_date, advertiser.agg.is_visible, advertiser.agg.is_locked]);

  const handleUpdateAdvertiserItem = (field: string, value: any) => {
    if (view?.id) {
      for (const campaign of advertiser.campaigns) {
        for (const line of campaign.line_items) {
          updateLineItem({ lineItem: line, field, value: value, viewId: view.id });
        }
      }
    }
  };

  const isLastRow = (idx: number) => {
    if (showAll) {
      return idx === advertiser.campaigns.length - 1;
    }
    return idx === advertiser.campaigns.filter((c) => c.agg.is_visible).length - 1 || advertiser.campaigns.filter((c) => c.agg.is_visible).length == 1;
  };

  if (!showAll && !isVisible) {
    return null;
  }

  return (
    <>
      <tr
        className={`cursor-pointer hover:bg-gray-200 ${isExpanded ? 'border-yellow-500 border-4 border-b-0' : 'border-b-1'}`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <td className="w-[3%] min-w-[15px] border-r border-gray-201">
          <button
            className="rounded p-1 pl-2"
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        </td>
        <td className="w-[25%] px-6 py-1 whitespace-nowrap border-r border-gray-201">
          <div className='flex flex-col items-left'>
            <div className='flex items-center'>
              <div className="text-sm font-medium text-gray-900">
                {advertiser.name}
                <span className='text-xs text-gray-500 ml-2'>
                  {advertiser.source?.replace('_', ' ').toLowerCase()}
                </span>
              </div>
              <div>
                {showAll && (
                  <div className='flex items-center gap-2'>
                    <div
                      onClick={(e) => {e.stopPropagation(); setIsVisible(!isVisible); handleUpdateAdvertiserItem('is_visible', !isVisible);}}
                      className='cursor-pointer ml-2'
                    >
                      {isVisible ? <Eye className="h-4 w-4 bg-blue-500 hover:bg-blue-400 text-white rounded-sm p-0.5 cursor-pointer" /> : <EyeOff className="h-4 w-4 bg-gray-201 text-black hover:bg-gray-200 rounded-sm p-0.5 cursor-pointer" />}
                    </div>
                    <div
                      onClick={(e) => {e.stopPropagation(); setIsLocked(!isLocked); handleUpdateAdvertiserItem('is_locked', !isLocked);}}
                      className='cursor-pointer'
                    >
                      {isLocked ? <Lock className="h-4 w-4 bg-orange-501 text-white rounded-sm hover:bg-orange-300 p-0.5 cursor-pointer" /> : <Unlock className="h-4 w-4 bg-gray-201 text-black hover:bg-gray-200 rounded-sm p-0.5 cursor-pointer" />}
                    </div>
                  </div>
                )}
              </div>

            </div>

            <div className='flex items-center text-xs text-gray-500'>
              <div
                className={`w-2 h-2 rounded-full mr-1 ${advertiserStatus === 'on_track' ? 'bg-green-501' : advertiserStatus === 'ahead' ? 'bg-red-501' : advertiserStatus === 'behind' ? 'bg-yellow-500' : advertiserStatus === 'update' ? 'bg-orange-501' : 'bg-gray-500'}`}
              />
              {advertiserStatus.toLocaleLowerCase().replace('_', ' ')}
              <div className='w-full ml-4 bg-gray-400 rounded-full h-2 text-center'>
                <div
                  className='bg-blue-500 h-2 rounded-full text-gray-200 items-center justify-center'
                  style={{ width: `${Math.min(advertiser?.agg.percent_complete ?? 0, 100).toFixed(0)}%` }}
                />
              </div>
            </div>

          </div>
        </td>
        <td className="px-6 py-2 whitespace-nowrap border-r border-gray-201">
          <div className="text-sm text-gray-900 font-bold">
            $
            {advertiser.agg.budget.toLocaleString()}
          </div>
        </td>
        <td className="w-[150px] px-6 py-2 whitespace-nowrap border-r border-gray-201">
          <div className="text-sm text-gray-900 font-bold">
            $
            {advertiser.agg.daily_budget.toLocaleString()}
          </div>
        </td>
        <td className="px-6 py-2 whitespace-nowrap border-r border-gray-201">
          <div className="text-sm text-gray-900 font-bold">
            $
            {advertiser.agg.spend.toLocaleString()}
          </div>
        </td>
        <td className="px-6 py-2 whitespace-nowrap">
          <div className="text-sm text-gray-900 font-bold">
            {formatDateForDisplay(new Date(advertiser.agg.start_date + 'T00:00:00'))}
            {' '}
            -
            {formatDateForDisplay(new Date(advertiser.agg.end_date + 'T00:00:00'))}
          </div>
        </td>
      </tr>
      {isExpanded && advertiser.campaigns.map((campaign, index) => (
        <CampaignRow
          key={campaign.id}
          campaign={campaign}
          isLastRow={isLastRow(index)}
          view={view}
          showAll={showAll}
        />
      ))}
    </>
  );

};

export default AdvertiserRow;
